import { Typography, Grid, Divider } from "@mui/material";
import CardWrapper from "../card-wrapper";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

const Details = ({ profileData }: any) => {
  const reduxStaticData = useSelector((state: any) => state.staticData);

  const staticData = useMemo(() => {
    let cachedStaticData = {};
    try {
      cachedStaticData = JSON.parse(sessionStorage.getItem("static-data") || "{}");
    } catch (e) {
      cachedStaticData = {};
    }
    return { ...cachedStaticData, ...reduxStaticData };
  }, [reduxStaticData]);

  const LIST_ALIASES: Record<string, string[]> = {
    countries: ["countries", "country"],
    states: ["states", "state"],
    districts: ["districts", "cities", "district", "city"],
    professions: ["professions", "profession"],
    qualifications: ["qualifications", "qualification"],
    economicStatuses: ["economicStatuses", "economicStatuses", "economicStatus"],
    physicalStatuses: ["physicalStatuses", "physicalStatuses", "physicalStatus"],
    motherTongues: ["motherTongues", "motherTongue", "religions", "religion"],
    castes: ["castes", "caste"],
    subCastes: ["subCastes", "subCastesAD", "subCaste"],
    stars: ["stars", "star"],
    raasis: ["raasis", "rasi", "raasi"],
    paadhams: ["paadhams", "padham", "paadham"],
    lagnams: ["lagnams", "lagnam"],
    familyTypes: ["familyTypes", "familyType"],
    maritalStatuses: ["maritalStatuses", "maritalStatus"],
    genders: ["genders", "gender"],
  };

  const getList = (listKey: string) => {
    const keys = LIST_ALIASES[listKey] || [listKey];
    for (const key of keys) {
      const list = staticData?.[key];
      if (Array.isArray(list) && list.length) return list;
    }
    return [];
  };

  const getNameById = (listKey: string, id: any) => {
    if (id === null || id === undefined || id === "") return "-";
    const list = getList(listKey);
    if (!list.length) return id;
    const matched = list.find((item: any) => String(item?.id) === String(id));
    return matched?.name || id;
  };

  const getDisplayValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  const cards = [
    {
      title: 'Personal Details',
      data: [
        { label: 'Date Of Birth', value: profileData.dateOfBirth },
        { label: 'Birth Time', value: profileData.birthTime },
        { label: 'Birth Place', value: profileData.birthPlace },
        { label: 'Birth Order', value: profileData.birthOrder },
        { label: 'Physical Status', value: getNameById('physicalStatuses', profileData.physicalStatus) },
        { label: 'Economic Status', value: getNameById('economicStatuses', profileData.economicStatus) },
        { label: 'Weight', value: profileData.weight ? `${profileData.weight} kg` : '-' },
        { label: 'Height', value: profileData.height ? `${profileData.height} inch` : '-' },
        { label: 'Body Type', value: getDisplayValue(profileData.bodyType) },
      ]
    },
    {
      title: 'Professional Details',
      data: [
        { label: 'Profession', value: getNameById('professions', profileData.profession) },
        { label: 'Qualification', value: getNameById('qualifications', profileData.qualification) },
        { label: 'Salary', value: getDisplayValue(profileData.salary) },
      ]
    },
    {
      title: 'Current Address Details',
      data: [
        { label: 'Country', value: getNameById('countries', profileData.country) },
        { label: 'State', value: getNameById('states', profileData.state) },
        { label: 'District', value: getNameById('districts', profileData.city) },
        { label: 'Address', value: getDisplayValue(profileData.address) },
      ]
    },
    {
      title: 'Native Address Details',
      data: [
        { label: 'Country', value: getNameById('countries', profileData.nativeCountry) },
        { label: 'State', value: getNameById('states', profileData.nativeState) },
        { label: 'District', value: getNameById('districts', profileData.nativeDistrict) },
        { label: 'Address', value: getDisplayValue(profileData.nativeAddress) },
      ]
    },
    {
      title: 'Astrology Details',
      data: [
        { label: 'Star', value: getNameById('stars', profileData.star) },
        { label: 'Raasi', value: getNameById('raasis', profileData.raasi) },
        { label: 'Paadham', value: getNameById('paadhams', profileData.paadham) },
        { label: 'Sevvai', value: getDisplayValue(profileData.sevvai) },
        { label: 'Raagu Kethu', value: getDisplayValue(profileData.raghuKedhu) },
        { label: 'Lagnam', value: getNameById('lagnams', profileData.lagnam) },
      ]
    },
    {
      title: 'Family Details',
      data: [
        { label: 'Father Name', value: getDisplayValue(profileData.father) },
        { label: 'Father`s Occupation', value: getDisplayValue(profileData.fatherOccupation) },
        { label: 'Mother Name', value: getDisplayValue(profileData.mother) },
        { label: 'Mother`s Occupation', value: getDisplayValue(profileData.motherOccupation) },
        { label: 'No of sisters', value: getDisplayValue(profileData.noOfSisters) },
        { label: 'No of sisters married', value: getDisplayValue(profileData.noOfSistersMarried) },
        { label: 'No of Brothers', value: getDisplayValue(profileData.noOfBrothers) },
        { label: 'No of Brothers married', value: getDisplayValue(profileData.noOfBrothersMarried) },
        { label: 'Family Type', value: getNameById('familyTypes', profileData.familyType) },
      ]
    },
    {
      title: 'Community Details',
      data: [
        { label: 'Mother Toungue', value: getNameById('motherTongues', profileData.religion) },
        { label: 'Caste', value: getNameById('castes', profileData.caste) },
        { label: 'Sub Caste', value: getNameById('subCastes', profileData.subCaste) },
        { label: 'Sub Caste Additional', value: getDisplayValue(profileData.subCasteAD || profileData.subCasteAdditional) },
      ]
    },
    {
      title: 'Additional Details',
      data: [
        { label: 'Property Details', value: getDisplayValue(profileData.property) },
      ]
    }
  ]

  const fullName =
    getDisplayValue(profileData.fullName) !== "-"
      ? profileData.fullName
      : `${profileData.firstName || ""} ${profileData.lastName || ""}`.trim() || "Profile";

  const ageText = profileData.age ? `${profileData.age} years` : "-";
  const maritalStatusText = getNameById('maritalStatuses', profileData.maritalStatus);
  const genderText = getNameById('genders', profileData.gender);

  return (
    <>
      <CardWrapper title={fullName}>
        <Typography color="textSecondary">
          {`${ageText}, ${maritalStatusText}, ${genderText}`}
        </Typography>
      </CardWrapper>
      <CardWrapper title="About Me">
        <Typography color="textSecondary" textAlign="justify">
          {getDisplayValue(profileData.aboutYou)}
        </Typography>
      </CardWrapper>
      {
        cards.map(({ title, data }: any, cardIndex: number) => {
          return <CardWrapper key={cardIndex} title={title}>
            {data.map(({ label, value }: any, index: number) => {
              return <React.Fragment key={index}>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography>{label}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>{value}</Typography>
                  </Grid>
                </Grid>
                <Divider />
              </React.Fragment>
            })}
          </CardWrapper>
        })
      }
    </>
  );
};

export default Details;
